/**
 * Compact hand deep-links via hash: `#/h/<base64url(json)>`.
 *
 * Limits (keep URLs shareable in Discord/Kakao):
 * - Soft max ~1800 characters of encoded payload (encode returns null if over)
 * - Max 40 actions round-tripped
 * - Best-effort draft share — not a stable public API; schema `v: 1`
 * - Prefer screenshot card + text copy as primary share; link is optional
 *
 * Does not encode locale / UI step — only HandState essentials.
 *
 * Soft G: optional ending record rides in the same hash payload as optional
 * field `e` (no new route / path / query). Absent when empty → the encoded
 * payload is byte-identical to pre-Soft G links; old links (no `e`) decode as
 * before. If `e` would push the payload past the limit, the ending is dropped
 * from the link (never break the link).
 *
 * Soft I2b: optional takeaway (과정 한 줄) rides as optional field `t`. Absent
 * when empty → byte-identical to pre-I2b links; old links (no `t`) decode as
 * before. Over budget: drop ending first, then `t` (toast each time).
 */

import { compactEnding, expandEnding, type CompactEnding } from './endingRecord'
import { sanitizeHand } from './poker'
import type { HandState } from './types'

const MAX_ACTIONS = 40
const MAX_PAYLOAD_CHARS = 1800

type CompactV1 = {
  v: 1
  n: number
  sb: number
  bb: number
  hs: number
  su: 'bb' | 'chips'
  hc?: number
  hp: string
  a?: number
  es?: number
  p?: number
  h: [string | null, string | null]
  /** knownHoles: pos → [c0, c1] — omit when empty */
  kh?: Record<string, [string | null, string | null]>
  bf: [string | null, string | null, string | null]
  bt: string | null
  br: string | null
  x: { s: string; a: string; t: string; z?: string }[]
  /** Soft G: optional ending record — omitted when empty (no new bytes). */
  e?: CompactEnding
  /** Soft I2b: optional takeaway — omitted when empty (no new bytes). */
  t?: string
}

function toBase64Url(bytes: Uint8Array): string {
  let bin = ''
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]!)
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function fromBase64Url(s: string): Uint8Array | null {
  try {
    const pad = s.length % 4 === 0 ? '' : '='.repeat(4 - (s.length % 4))
    const b64 = s.replace(/-/g, '+').replace(/_/g, '/') + pad
    const bin = atob(b64)
    const out = new Uint8Array(bin.length)
    for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i)
    return out
  } catch {
    return null
  }
}

function utf8Encode(str: string): Uint8Array {
  return new TextEncoder().encode(str)
}

function utf8Decode(bytes: Uint8Array): string {
  return new TextDecoder().decode(bytes)
}

function compactHand(state: HandState): CompactV1 {
  const x = state.actions.slice(0, MAX_ACTIONS).map((act) => {
    const row: CompactV1['x'][number] = {
      s: act.street,
      a: act.actor,
      t: act.type,
    }
    if (act.size) row.z = act.size
    return row
  })
  const c: CompactV1 = {
    v: 1,
    n: state.playerCount,
    sb: state.smallBlind,
    bb: state.bigBlind,
    hs: state.heroStackBb,
    su: state.stackUnit,
    hp: state.heroPosition,
    h: [...state.holeCards],
    bf: [...state.board.flop],
    bt: state.board.turn,
    br: state.board.river,
    x,
  }
  if (state.stackUnit === 'chips') c.hc = state.heroStackChips
  if (state.ante != null && Number.isFinite(state.ante)) c.a = state.ante
  if (state.effectiveStackBb != null && Number.isFinite(state.effectiveStackBb)) {
    c.es = state.effectiveStackBb
  }
  if (state.potBb != null && Number.isFinite(state.potBb)) c.p = state.potBb
  const kh: NonNullable<CompactV1['kh']> = {}
  for (const [pos, holes] of Object.entries(state.knownHoles ?? {})) {
    if (!holes) continue
    if (!holes[0] && !holes[1]) continue
    if (pos === state.heroPosition) continue
    kh[pos] = [holes[0], holes[1]]
  }
  if (Object.keys(kh).length) c.kh = kh
  // Soft I2b: takeaway before ending — empty adds no bytes; old links omit `t`.
  const takeaway = state.takeaway?.trim()
  if (takeaway) c.t = takeaway
  // Soft G: appended last and only when pressed — empty ending adds no bytes.
  // Without a process line there is nothing for the ending to follow → omit.
  const e = state.actions.length > 0 ? compactEnding(state.ending) : undefined
  if (e) c.e = e
  return c
}

function expandCompact(c: CompactV1): unknown {
  return {
    playerCount: c.n,
    smallBlind: c.sb,
    bigBlind: c.bb,
    heroStackBb: c.hs,
    heroStackChips: c.hc,
    stackUnit: c.su,
    heroPosition: c.hp,
    ante: c.a,
    effectiveStackBb: c.es,
    potBb: c.p,
    holeCards: c.h,
    knownHoles: c.kh ?? {},
    board: {
      flop: c.bf,
      turn: c.bt,
      river: c.br,
    },
    actions: (c.x ?? []).map((row, i) => ({
      id: `link-${i}`,
      street: row.s,
      actor: row.a,
      type: row.t,
      size: row.z,
    })),
    // Soft I2b: old links have no `t` → no takeaway (renders as before).
    takeaway: typeof c.t === 'string' ? c.t : '',
    // Soft G: old links have no `e` → undefined → no ending (renders as before).
    ending: c.e ? expandEnding(c.e) : undefined,
  }
}

function encodePayload(state: HandState): string | null {
  try {
    const json = JSON.stringify(compactHand(state))
    const payload = toBase64Url(utf8Encode(json))
    if (payload.length > MAX_PAYLOAD_CHARS) return null
    return payload
  } catch {
    return null
  }
}

/** True when compactHand would include at least one villain knownHoles entry. */
function hasEncodableKnownHoles(state: HandState): boolean {
  for (const [pos, holes] of Object.entries(state.knownHoles ?? {})) {
    if (!holes) continue
    if (!holes[0] && !holes[1]) continue
    if (pos === state.heroPosition) continue
    return true
  }
  return false
}

export type HandLinkEncodeResult =
  | {
      ok: true
      payload: string
      strippedKnownHoles: boolean
      strippedEnding?: boolean
      /** Soft I2b: takeaway (`t`) left out to keep the link in budget. */
      strippedTakeaway?: boolean
    }
  | { ok: false; reason: 'tooLong' }

/**
 * Encode hand into a hash payload (without `#/h/` prefix).
 * Tries full state first; if over budget, retries without villain knownHoles
 * and reports `strippedKnownHoles: true` so UI can warn (never silent drop).
 */
export function encodeHandLink(state: HandState): HandLinkEncodeResult {
  const full = encodePayload(state)
  if (full) return { ok: true, payload: full, strippedKnownHoles: false }

  const hasEnding = Boolean(state.ending)
  const hasTakeaway = Boolean(state.takeaway?.trim())

  // Soft I2b / Soft G: over budget → drop ending first, then takeaway (`t`),
  // then villain knownHoles. Never break the link when a quieter payload fits.
  if (hasEnding) {
    const noEnding: HandState = { ...state, ending: undefined }
    const withoutEnding = encodePayload(noEnding)
    if (withoutEnding) {
      return { ok: true, payload: withoutEnding, strippedKnownHoles: false, strippedEnding: true }
    }
    if (hasTakeaway) {
      const noEndingOrT: HandState = { ...noEnding, takeaway: '' }
      const withoutT = encodePayload(noEndingOrT)
      if (withoutT) {
        return {
          ok: true,
          payload: withoutT,
          strippedKnownHoles: false,
          strippedEnding: true,
          strippedTakeaway: true,
        }
      }
      if (hasEncodableKnownHoles(noEndingOrT)) {
        const stripped = encodePayload({ ...noEndingOrT, knownHoles: {} })
        if (stripped) {
          return {
            ok: true,
            payload: stripped,
            strippedKnownHoles: true,
            strippedEnding: true,
            strippedTakeaway: true,
          }
        }
      }
      return { ok: false, reason: 'tooLong' }
    }
    if (hasEncodableKnownHoles(noEnding)) {
      const stripped = encodePayload({ ...noEnding, knownHoles: {} })
      if (stripped) {
        return { ok: true, payload: stripped, strippedKnownHoles: true, strippedEnding: true }
      }
    }
    return { ok: false, reason: 'tooLong' }
  }

  if (hasTakeaway) {
    const noT: HandState = { ...state, takeaway: '' }
    const withoutT = encodePayload(noT)
    if (withoutT) {
      return { ok: true, payload: withoutT, strippedKnownHoles: false, strippedTakeaway: true }
    }
    if (hasEncodableKnownHoles(noT)) {
      const stripped = encodePayload({ ...noT, knownHoles: {} })
      if (stripped) {
        return { ok: true, payload: stripped, strippedKnownHoles: true, strippedTakeaway: true }
      }
    }
    return { ok: false, reason: 'tooLong' }
  }

  if (hasEncodableKnownHoles(state)) {
    const stripped = encodePayload({ ...state, knownHoles: {} })
    if (stripped) {
      return { ok: true, payload: stripped, strippedKnownHoles: true }
    }
  }
  return { ok: false, reason: 'tooLong' }
}

/** Decode payload from `#/h/<payload>`. */
export function decodeHandLink(payload: string): HandState | null {
  if (!payload || payload.length > MAX_PAYLOAD_CHARS + 200) return null
  const bytes = fromBase64Url(payload)
  if (!bytes) return null
  try {
    const raw = JSON.parse(utf8Decode(bytes)) as CompactV1
    if (!raw || raw.v !== 1) return null
    return sanitizeHand(expandCompact(raw))
  } catch {
    return null
  }
}

export type HandShareUrlResult =
  | {
      ok: true
      url: string
      strippedKnownHoles: boolean
      strippedEnding?: boolean
      strippedTakeaway?: boolean
    }
  | { ok: false; reason: 'tooLong' }

/** Full shareable URL for current origin + hand hash. */
export function buildHandShareUrl(state: HandState): HandShareUrlResult {
  const encoded = encodeHandLink(state)
  if (!encoded.ok) return encoded
  const base = `${window.location.origin}${window.location.pathname}`
  return {
    ok: true,
    url: `${base}#/h/${encoded.payload}`,
    strippedKnownHoles: encoded.strippedKnownHoles,
    ...(encoded.strippedEnding ? { strippedEnding: true } : {}),
    ...(encoded.strippedTakeaway ? { strippedTakeaway: true } : {}),
  }
}

